/**
 * Sponsor invite campaign — timing and routes (mirrored in Edge `process-sponsor-invite-campaign`).
 *
 * **Minute test delays** (same idea as `process-unclaimed-reminders` / claim campaign: one calendar day
 * compresses to one minute for local testing — here we use fixed **3 min** and **7 min** after start
 * for email 2 and 3, matching production **3 days** and **7 days**).
 *
 * - **App / Vite:** `VITE_SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS=true` in `.env.local` (same pattern as claim
 *   campaign; Vite exposes `VITE_*` to the client bundle).
 * - **Jest:** `process.env.VITE_SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS` with `resetModules` (see tests).
 * - **Edge:** secret `SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS=true` on `process-sponsor-invite-campaign` and
 *   `send-sponsor-invite-test-email` (and optionally `start-sponsor-invite-campaign` for parity).
 *   Run cron at least every minute while testing.
 */

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * 60 * 1000;

function resolveSponsorInviteUseMinuteTestDelays(): boolean {
  return (
    typeof process !== 'undefined' &&
    process.env.VITE_SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS === 'true'
  );
}

export const SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS = resolveSponsorInviteUseMinuteTestDelays();

export const SPONSOR_INVITE_EMAIL_2_DELAY_MS = SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS
  ? 3 * MINUTE_MS
  : 3 * DAY_MS;

export const SPONSOR_INVITE_EMAIL_3_DELAY_MS = SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS
  ? 7 * MINUTE_MS
  : 7 * DAY_MS;

export const SPONSOR_INVITE_MAX_STAGE = 3;

/** Fixed signup path segment (CTA URL is not admin-editable). */
export const SPONSOR_INVITE_SIGNUP_PATH = '/startup-signup';

/** Click tracker before signup (logs link_clicked_at). */
export const SPONSOR_INVITE_CLICK_PATH = '/sponsor-invite';

export const SPONSOR_INVITE_QUERY_PARAM = 'sponsor_invite';

/**
 * Prefix marking a "Send test email" preview link (Task 7). Real invite tokens are UUIDs and
 * never start with this, so the landing page can render a safe preview without a DB lookup.
 * Keep in sync with the literal used in `send-sponsor-invite-test-email`.
 */
export const SPONSOR_INVITE_PREVIEW_TOKEN_PREFIX = 'preview-';

export function isSponsorInvitePreviewToken(token: string | null | undefined): boolean {
  return typeof token === 'string' && token.startsWith(SPONSOR_INVITE_PREVIEW_TOKEN_PREFIX);
}

export type SponsorInviteCampaignStatus =
  | 'active'
  | 'completed'
  | 'stopped'
  | 'failed';

export type SponsorInviteRecipientFilter = 'all' | 'active' | 'converted' | 'stopped';

export const SPONSOR_INVITE_RECIPIENT_FILTER_LABELS: Record<SponsorInviteRecipientFilter, string> = {
  all: 'All',
  active: 'Active sequence',
  converted: 'Converted',
  stopped: 'Stopped',
};

export const SPONSOR_INVITE_PAGE_SIZE = 25;
